import React, { useState, useContext, useRef } from "react";
import { StepContext, StepContextProvider } from "./context";

type StepComponentPropsT = Partial<{
  data: Record<string, unknown>;
  valid: boolean;
  touched: boolean;
  setData: <ValueT>(key: string, value: ValueT) => void;
  setValid: (status: boolean) => void;
  setTouched: (status: boolean) => void;
}>;

type StepWithComponentT<DataT> = {
  name?: string;
  data?: Record<string, DataT>;
  valid?: boolean;
  Component: React.FunctionComponent<Partial<StepComponentPropsT>>;
}

type StepWithElementT<DataT> = {
  name?: string;
  data?: Record<string, DataT>;
  valid?: boolean;
  element: React.ReactElement;
}

type StepT<DataT = unknown> =  StepWithComponentT<DataT> | StepWithElementT<DataT>;

export function useStepFormApi(steps: StepT[], initiallyAllAccessible = false) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [data, setData] = useState<Record<string, unknown>[]>(
    steps.map((step) => step.data || {})
  );
  const [touched, setTouched] = useState<boolean[]>(steps.map(() => false));
  const [validity, setValidity] = useState<boolean[]>(
    steps.map((step) => step.valid || false)
  );
  const [accessibility, setAccessibility] = useState<boolean[]>(
    steps.map((_, index) => index === 0 || initiallyAllAccessible)
  );

  const reset = () => {
    setCurrentStepIndex(0);
    setData(steps.map((step) => step.data || {}));
    setTouched(steps.map(() => false));
    setValidity(steps.map((step) => step.valid || false));
    setAccessibility(
      steps.map((_, index) => index === 0 || initiallyAllAccessible)
    );
  };

  const setStepData = <ValueT,>(step: number, key: string, value: ValueT) => {
    const dataCopy = [...data];
    dataCopy[step] = {
      ...dataCopy[step],
      [key]: value,
    };
    setData(dataCopy);
  };

  const setStepAccessibility = (step: number, status: boolean) => {
    const accessibilityCopy = [...accessibility];
    accessibilityCopy[step] = status;
    setAccessibility(accessibilityCopy);
  };

  const setStepValidity = (step: number, status: boolean) => {
    const validityCopy = [...validity];
    validityCopy[step] = status;
    setValidity(validityCopy);
    const nextStep = step + 1;
    if (status && nextStep <= steps.length - 1) {
      setStepAccessibility(step + 1, true);
    }
  };

  const setStepTouched = (step: number, status: boolean) => {
    const touchedCopy = [...touched];
    touchedCopy[step] = status;
    setTouched(touchedCopy);
  };

  const components = steps.map((step, thisStepIndex) => {
    const Component = 'Component' in step ? step.Component : null;
    const element = 'element' in step ? step.element : null;
    const currentStepData = data[thisStepIndex];
    const currentStepValid = validity[thisStepIndex];
    const currentStepTouched = touched[thisStepIndex];
    const setCurrentStepData = <ValueT,>(key: string, value: ValueT) =>
      setStepData(thisStepIndex, key, value);
    const setCurrentStepValid = (status: boolean) =>
      setStepValidity(thisStepIndex, status);
    const setCurrentStepTouched = (status: boolean) =>
      setStepTouched(thisStepIndex, status);
    const props = {
      data: currentStepData,
      setData: setCurrentStepData,
      valid: currentStepValid,
      touched: currentStepTouched,
      setValid: setCurrentStepValid,
      setTouched: setCurrentStepTouched
    }
    return (
      <StepContextProvider
        data={currentStepData}
        touched={currentStepTouched}
        valid={currentStepValid}
        setData={setCurrentStepData}
        setTouched={setCurrentStepTouched}
        setValid={setCurrentStepValid}
      >
        {element ? React.cloneElement(element, props) : null}
        {Component && !element ? (
          <Component
            data={currentStepData}
            setData={setCurrentStepData}
            valid={currentStepValid}
            touched={currentStepTouched}
            setValid={setCurrentStepValid}
            setTouched={setCurrentStepTouched}
          />
        ): null}
        
      </StepContextProvider>
    );
  });

  const currentStepContent = components[currentStepIndex];
  const currentStepData = data[currentStepIndex];
  const currentStepValid = validity[currentStepIndex];
  const currentStepTouched = touched[currentStepIndex];

  const nextStepEnabled = currentStepValid;
  const previousStepEnabled = true;
  const finishEnabled = validity.every((v) => v);

  const gotoNextStep = () => {
    if (!nextStepEnabled) return false;
    gotoStep(currentStepIndex + 1);
    return true;
  };

  const gotoPreviousStep = () => {
    if (!previousStepEnabled) return false;
    gotoStep(currentStepIndex - 1);
    return true;
  };

  const gotoStep = (index: number) => {
    if (!accessibility[index]) return;
    setCurrentStepIndex(index);
  };

  return {
    currentStepIndex,
    steps,
    data,
    touched,
    validity,
    accessibility,
    gotoStep,
    gotoNextStep,
    gotoPreviousStep,
    setStepData,
    setStepValidity,
    setStepTouched,
    currentStepContent,
    currentStepData,
    currentStepValid,
    currentStepTouched,
    nextStepEnabled,
    previousStepEnabled,
    finishEnabled,
    reset,
  };
}

export function useStepData<ValueT>(
  name: string,
  initialValue?: ValueT
): [ValueT, (value: ValueT) => void] {
  const { data, setData } = useContext(StepContext);

  // Save the reference to the initial initialValue
  const initialValueRef = useRef<ValueT>();

  if (initialValueRef.current === undefined) {
    initialValueRef.current = initialValue;
  }

  return [
    (data[name] || initialValueRef.current) as ValueT,
    (value: ValueT) => setData(name, value),
  ];
}

export function useStepValidity(): [boolean, (valid: boolean) => void] {
  const { valid, setValid } = useContext(StepContext);
  return [valid, setValid];
}

export function useStepTouched(): [boolean, (touched: boolean) => void] {
  const { touched, setTouched } = useContext(StepContext);
  return [touched, setTouched];
}
